import { useUserDeviceSharedWithList } from 'api/goodtrust/user/device'
import { useUserDocumentSharedWithList } from 'api/goodtrust/user/document'
import { useUserSiteSharedWithList } from 'api/goodtrust/user/site'
import { Button } from 'components/button/Button'
import { useDashboardSideModal } from 'components/content/dashboardSideModal/utils'
import { DeviceCard } from 'components/device/deviceCard/DeviceCard'
import { DocumentCard } from 'components/document/documentCard/DocumentCard'
import { SectionEmptyState } from 'components/sectionEmptyState/SectionEmptyState'
import { SiteCard } from 'components/site/siteCard/SiteCard'
import { BlockSpinner } from 'components/spinner/Spinner'
import { WizardContentHeading } from 'components/wizard/wizardContent/WizardContent'
import { SWizardGrid } from 'components/wizard/wizardGridCard/WizardGridCard'
import { useTranslation } from 'next-i18next'
import { VFC } from 'react'
import { getPaginationMeta } from 'utils/pagination'

function flatMapPaginated<T, R>(
  pageSource: { data?: { json?: { items?: T[] } }[] },
  map: (item: T) => R
): R[] | undefined {
  return pageSource.data?.flatMap((page) => page.json?.items?.map(map) ?? [])
}

export const SharedTab: VFC = () => {
  const { t } = useTranslation('digital_vault')
  const { t: tCommon } = useTranslation()
  const sites = useUserSiteSharedWithList()
  const documents = useUserDocumentSharedWithList()
  const devices = useUserDeviceSharedWithList()

  const [sideModal, openSideModal] = useDashboardSideModal('DIGITAL_VAULT')

  const sources = [
    { swr: sites, meta: getPaginationMeta(sites) },
    { swr: documents, meta: getPaginationMeta(documents) },
    { swr: devices, meta: getPaginationMeta(devices) },
  ]

  const isLoading = sources.some((source) => source.meta.isLoadingInitial)

  if (isLoading) return <BlockSpinner />

  const nothingSharedYet = sources.every((source) => source.meta.isEmpty)
  const sourcesWithMoreData = sources.filter((source) => !source.meta.isReachingEnd)
  const isLoadingMore = sources.some((source) => source.meta.isLoadingMore)

  if (nothingSharedYet)
    return (
      <>
        {sideModal}
        <SectionEmptyState
          icon="DigitalVault"
          title={t('digital_vault.inside_page.shared.title')}
          description={t('digital_vault.inside_page.shared.desc')}
          buttons={
            <>
              <Button type="button" variant="secondary" onClick={() => openSideModal()}>
                {t('digital_vault.inside_page.shared.info_button')}
              </Button>
            </>
          }
        />
      </>
    )

  return (
    <>
      {sideModal}
      <WizardContentHeading title={t('digital_vault.inside_page.shared.list_title')} />
      <SWizardGrid>
        {flatMapPaginated(sites, (site) => (
          <SiteCard key={site.uuid} site={site} showType sharedWithMe />
        ))}
        {flatMapPaginated(documents, (document) => (
          <DocumentCard key={document.uuid} document={document} showType sharedWithMe />
        ))}
        {flatMapPaginated(devices, (device) => (
          <DeviceCard key={device.uuid} device={device} showType sharedWithMe />
        ))}
        {sourcesWithMoreData.length > 0 && (
          <Button
            type="button"
            variant="secondary"
            size="large"
            loading={isLoadingMore}
            onClick={() => sourcesWithMoreData.forEach((source) => source.meta.handleLoadMore?.())}
            block
            data-testid="load-more-button"
          >
            {tCommon('common.load_more')}
          </Button>
        )}
      </SWizardGrid>
    </>
  )
}
